<script setup>
import Layout from "../Layouts/Customer.vue";
import Checkbox from "primevue/checkbox";
import Button from "primevue/button";
import {ref} from "vue";
import {useForm} from "@inertiajs/inertia-vue3";
import moment from "moment/moment";
import {required$, true$} from "../utils/validators";
import ConsentSignature from "../Components/ConsentSignature.vue";
import {useVuelidate} from "@vuelidate/core";
import {scrollToFirstError} from "../utils/form";
import {dataURLtoFile} from "../utils/file-utils";
import { useToast } from "primevue/usetoast";
import {Inertia} from "@inertiajs/inertia";

const props = defineProps([
    'customer',
    'menuItems',
    'flash',
]);
const toast = useToast()
const consent = ref(props.customer?.alma_primex_consent)
const consentSlug = ref('alma-primex-consents')
const isEdit = ref(Boolean(consent.value))
const submitting = ref(false)
const consentSignature = ref(null)
const date = ref(
    isEdit.value ? moment(consent.value.updated_at) : moment()
)
const form = useForm({
    covered_areas: isEdit.value ? consent.value.covered_areas : [],
    signature_key: null,
    customer_uuid: props.customer.uuid,
    is_agree_signature: isEdit.value,
    signature_img: isEdit.value ? consent.value.signature_img : null,
    has_signature: null,
    date: date.value.format('DD.MM.YYYY'),
});

const rules = {
    form: {
        covered_areas: {
            required$,
        },
        is_agree_signature: {
            true$,
        },
        signature_key: {
            custom$: {
                $validator: function(value) {
                    return form.has_signature !== false
                },
                $message: 'Dieses Feld ist erforderlich',
            }
        }
    }
}

const v$ = useVuelidate(rules, {
    form,
})

const submit = async (e, isDownload) => {
    let signatureData = null
    const signaturePad = consentSignature.value.getSignaturePad()

    if (signaturePad) {
        const { isEmpty, data } = signaturePad.saveSignature()

        form.has_signature = ! isEmpty
        signatureData = data
    }

    v$.value.$validate()

    if (v$.value.$errors.length) {
        scrollToFirstError()
        toast.add({
            severity: "error",
            summary: "Error",
            life: 5000,
        });

        return
    }

    submitting.value = true

    if (signatureData) {
        const { key:signatureKey } = await Vapor.store(dataURLtoFile(signatureData), {
            progress: progress => {
            }
        })

        form.signature_key = signatureKey
    }

    const postUrl = isEdit.value
        ? `/${consentSlug.value}/${consent.value.uuid}`
        : `/${consentSlug.value}`

    form.post(
        postUrl,
        {
            preserveState: false,
            onError: (errors) => {
                toast.add({
                    severity: "error",
                    summary: "Error",
                    life: 5000,
                });
            },

            onSuccess: (page) => {
                toast.add({
                    severity: "success",
                    summary: "Aktualisiert",
                    life: 5000,
                });

                if (isDownload) {
                    window.location.href = `/${consentSlug.value}/${consent.value.uuid}/download`
                }
            },

            onFinish: (visit) => {
                submitting.value = false;
            }
        }
    )
}

const download = async () => {
    if (! submitting.value) {
        await submit(null, true)
    }
}
</script>
<template>
    <Layout :customer="customer" :menu-items="menuItems" :flash="flash">
        <div class="px-14 py-7">
            <div class="relative mb-9">
                <h1 class="text-black text-base font-bold">
                    Einverständniserklärung und Vertrag für die Ultraschall- und
                    Radiofrequenzbehandlung mit dem Alma PrimeX
                </h1>

                <div v-if="isEdit" class="absolute top-0 right-0">
                    <i class="pi pi-file-pdf text-[#C5C6CC] cursor-pointer"
                       @click="download"></i>
                </div>
            </div>

            <div class="text-sm">
                <h3 class="text-black font-bold">Einführung</h3>

                <div class="flex flex-col gap-3 mb-9">
                    <p>Die Ultraschall- und Radiofrequenzbehandlung mit dem Alma PrimeX ist ein nicht-invasives Verfahren zur
                        Körperkonturierung und Reduktion von Fettzellen. Die Behandlung erfolgt durch gezielte Ultraschall- und
                        Radiofrequenzwellen, die Fettzellen destabilisieren und deren Abbau durch das Immunsystem fördern.</p>
                </div>

                <ul class="mb-9">
                    <li>
                        <h3 class="text-sm font-bold my-5">1. Vertragsgegenstand</h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Der Kunde beauftragt VIGORY® mit der Durchführung von Behandlungen mittels des Ultraschallgeräts
                                Alma PrimeX zur Reduktion von Fettdepots.
                            </p>

                            <p>
                                Umfang und Preise unserer Leistungen sind auf unserer Website ersichtlich und können unter dem
                                folgenden Link abgerufen werden [Einfügen Link/Verweis]. Es gelten die im Zeitpunkt der Buchung
                                ausgewiesenen Preise.
                            </p>

                            <p>Zu behandelnde Bereiche:</p>

                            <div>
                                <div class="flex flex-col gap-2">
                                    <div v-for="value in [
                                    'Bauch',
                                    'Gesäss',
                                    'Hüfte',
                                    'Oberschenkel',
                                    'Rücken',
                                    'Arme'
                                ]">
                                        <div class="flex items-center gap-4">
                                            <Checkbox
                                                :invalid="v$.form.covered_areas.$error"
                                                v-model="form.covered_areas"
                                                :value="value"
                                                @change="v$.form.covered_areas.$touch"></Checkbox>

                                            <div>{{ value }}</div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="v$.form.covered_areas.$error" class="text-sm text-[#dc2626]">
                                    {{ v$.form.covered_areas.$errors[0].$message }}
                                </div>
                            </div>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">2. Beschreibung des Verfahrens</h3>

                        <div class="flex flex-col gap-3">
                            <p>Vor der Behandlung wird der zu behandelnde Bereich markiert und mit einem speziellen Öl vorbereitet.
                                Das Alma PrimeX Gerät wird auf die Haut aufgesetzt und Ultraschall- und Radiofrequenzwellen werden
                                in die tieferen Hautschichten gesendet. Die Dauer der Behandlung hängt von der Größe des behandelten
                                Bereichs ab, beträgt jedoch in der Regel zwischen 30 und 60 Minuten.</p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            3. Risiken und Nebenwirkungen
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Wie bei jeder Behandlung gibt es potenzielle Risiken und Nebenwirkungen, die auftreten können.
                                Während der Behandlung kann ein Hitzegefühl verspürt werden. Rötungen, Schwellungen oder
                                Blutergüsse können im behandelten Bereich auftreten. Ein vorübergehendes Taubheitsgefühl, ein
                                Kribbeln der Haut, leichte bis mässige Schmerzen oder Unwohlsein während oder nach der Behandlung
                                sind möglich. Seltene Fälle von Empfindungsstörungen, Verbrennungen oder Blasenbildung der Haut,
                                oder Veränderungen der Hauttextur oder -farbe sind bekannt.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            4. Alternativen zu Behandlungen mit Ultraschall und Radiofrequenz
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Es gibt verschiedene Alternativen zur Alma PrimeX-Behandlung, einschliesslich Diät und
                                Bewegung, Kryolipolyse sowie andere ästhetische Verfahren wie Fettabsaugung oder nichtinvasive Laserbehandlungen.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            5. Vorbehalte und Gesundheitsschutz
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                VIGORY behält sich das Recht vor, die Behandlung abzulehnen oder abzubrechen, insbesondere wenn
                                gesundheitliche Bedenken vorliegen.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            6. Leistungsinhalt und Haftung
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Es kann bis zu 3 Monate dauern, bis die endgültigen Ergebnisse der Behandlung sichtbar sind. In seltenen
                                Fällen kann der Erfolg gering oder ausbleibend sein.
                            </p>

                            <p>
                                Der Leistungsinhalt umfasst die fachgerechte Durchführung der Behandlung. Das Behandlungsergebnis
                                kann sowohl von der Behandlung als auch von der Nachsorge des Kunden beeinflusst werden und hängt
                                insbesondere vom individuellen Heilungsverlauf ab. VIGORY garantiert keinen spezifischen
                                Behandlungserfolg, inkl. Fettreduktion.
                            </p>

                            <p>
                                VIGORY haftet gemäss den gesetzlichen Vorschriften für Verletzungen von vertraglichen und
                                ausservertraglichen Pflichten. Schadenersatz wird bei Vorsatz und grober Fahrlässigkeit geleistet. Bei
                                leichter Fahrlässigkeit haftet VIGORY nur bei Verletzung von Leben, Körper oder Gesundheit sowie bei
                                Verletzung wesentlicher Vertragspflichten. In diesen Fällen ist die Haftung auf den vorhersehbaren,
                                typischerweise eintretenden Schaden begrenzt. VIGORY haftet nicht für Schäden, die auf Umstände
                                zurückzuführen sind, für die der Kunde verantwortlich ist.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            7. Terminverschiebungen und -absagen
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Vereinbarte Folgetermine sind verbindlich. VIGORY stellt Personal und technische Ausstattung für die
                                Termine bereit. Terminverschiebungen sind kostenfrei, wenn sie mindestens 24 Stunden vor dem
                                vereinbarten Termin erfolgen.
                            </p>
                            <p>
                                Bei Absagen innerhalb von 24 Stunden vor dem Termin oder bei Nichterscheinen kann VIGORY die
                                vereinbarte Vergütung verlangen. Einsparungen oder anderweitige Verwendung der Kapazitäten werden
                                berücksichtigt.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            8. Datenschutz
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                VIGORY erhebt und bearbeitet Personendaten des Kunden nach Massgabe der Datenschutzerklärung,
                                unter folgendem Link abgerufen werden kann: https://www.vigory.ch/datenschutz
                            </p>

                            <p>
                                Der Kunde stimmt der fotografischen Dokumentation der behandelten Bereiche und der anonymen
                                Veröffentlichung der Ergebnisse zu Werbezwecken zu.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            9. Zustimmung zur Behandlung
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Ich bestätige hiermit, dass ich die oben genannten Informationen zur Alma PrimeX-Behandlung gelesen
                                und verstanden habe. Mir ist bewusst, dass jeder Mensch individuell reagiert und gewisse Nebeneffekte
                                auftreten können und dass das Behandlungsergebnis negativ beeinträchtigt werden kann, wenn das
                                definierte Therapiekonzept nicht zu Ende geführt wird. Ich kenne die Risiken und möglichen
                                Nebenwirkungen und willige freiwillig und nach ausreichender Überlegung in die Durchführung der
                                Behandlungen ein. Ich hatte die Gelegenheit, Fragen zu stellen, und diese wurden zu meiner
                                Zufriedenheit beantwortet.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            10. Haftungsausschluss
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Ich verstehe, dass das Ergebnis der Behandlung variieren kann und keine spezifischen Resultate
                                garantiert werden können. Ich entbinde hiermit die behandelnde Fachkraft, sowie das Center von
                                jeglicher Haftung, soweit gesetzlich zulässig.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            11. Schlussbestimmungen
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Die Angaben im Anamnesefragebogen habe ich wahrheitsgemäss beantwortet.
                                Wir bitten Sie, uns umgehend über jegliche Änderungen Ihres Gesundheitszustandes zu informieren.
                                Dies umfasst, ist aber nicht beschränkt auf, neu diagnostizierte Erkrankungen, Änderungen bestehender
                                Krankheiten, die Einnahme neuer Medikamente oder Änderungen in der Medikation. Diese
                                Informationen sind entscheidend, um Ihre Behandlung optimal anzupassen. Bitte beachten Sie, dass wir
                                keinerlei Verantwortung für gesundheitliche Folgen übernehmen können, die auf nicht mitgeteilte
                                Änderungen zurückzuführen sind.
                            </p>

                            <p>
                                Vielen Dank für Ihr Verständnis und Ihre Kooperation.
                            </p>
                        </div>
                    </li>
                </ul>

                <div class="mb-9">
                    <ConsentSignature
                        ref="consentSignature"
                        :customer="customer"
                        :form="form"
                        :v$="v$"
                        :is-edit="isEdit"
                    />
                </div>

                <div class="flex justify-end">
                    <Button
                        @click="submit"
                        :loading="submitting"
                        label="speichern"
                        class="min-w-[100px] !bg-black !text-[#FFF053] !border-none"
                    />
                </div>
            </div>
        </div>
    </Layout>
</template>
